import React, { useRef, forwardRef, useImperativeHandle } from "react";
import { TextField, IconButton, Box } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";

export interface SimpleRichBoxHandle {
    insertTextAtCursor: (text: string) => void;
}

interface SimpleRichBoxProps {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    onFocus?: () => void;
    readOnly?: boolean;
}

const SimpleRichBox = forwardRef<SimpleRichBoxHandle, SimpleRichBoxProps>(
    ({ value, onChange, placeholder, onFocus, readOnly }, ref) => {
        const textRef = useRef<HTMLTextAreaElement>(null);

        const insertTextAtCursor = (text: string) => {
            const textarea = textRef.current;
            if (!textarea) return;

            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;

            const before = value.slice(0, start);
            const after = value.slice(end);
            const newValue = before + text + after;

            onChange(newValue);

            requestAnimationFrame(() => {
                textarea.focus();
                const newCursorPos = start + text.length;
                textarea.setSelectionRange(newCursorPos, newCursorPos);
            });
        };

        useImperativeHandle(ref, () => ({
            insertTextAtCursor,
        }));

        const wrapSelectionWithTags = (startTag: string, endTag: string) => {
            const textarea = textRef.current;
            if (!textarea) return;

            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const selected = value.slice(start, end);

            if (!selected) return;

            const wrapped = `${startTag}${selected}${endTag}`;
            const newValue = value.slice(0, start) + wrapped + value.slice(end);

            onChange(newValue);

            requestAnimationFrame(() => {
                textarea.focus();
                const newCursorPos = start + wrapped.length;
                textarea.setSelectionRange(newCursorPos, newCursorPos);
            });
        };


        return (
            <Box
                sx={{
                    border: "#d0d0d0",
                    borderRadius: "8px",
                    width: "100%",
                    overflow: "hidden",
                }}
            >
                {/* Toolbar */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        borderBottom: "1px solid #ccc",
                        px: 1,
                        py: 1,
                        backgroundColor: "#ffffff",
                    }}
                >
                    <IconButton
                        onClick={() => wrapSelectionWithTags("<strong>", "</strong>")}
                        size="small"
                        title="Bold"
                    >
                        <FormatBoldIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                        onClick={() => wrapSelectionWithTags("<em>", "</em>")}
                        size="small"
                        title="Italic"
                    >
                        <FormatItalicIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                        onClick={() => wrapSelectionWithTags("<u>", "</u>")}
                        size="small"
                        title="Underline"
                    >
                        <FormatUnderlinedIcon fontSize="small" />
                    </IconButton>

                </Box>

                {/* Text Area */}
                <TextField
                    multiline
                    minRows={3}
                    fullWidth
                    inputRef={textRef}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    variant="standard"
                    onFocus={onFocus}
                    disabled={readOnly}
                    InputProps={{
                        disableUnderline: true,
                        sx: {
                            px: 2,
                            py: 1,
                            backgroundColor: "#fcfcfc",
                        },
                    }}
                    sx={{
                        "& .MuiInputBase-root": {
                            borderRadius: 0,
                        },
                    }}
                />
            </Box>
        );
    }
);

export default SimpleRichBox;

// import React, { useRef } from "react";
// import { TextField, IconButton, Box } from "@mui/material";
// import FormatBoldIcon from "@mui/icons-material/FormatBold";
// import FormatItalicIcon from "@mui/icons-material/FormatItalic";
// import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";

// interface SimpleRichBoxProps {
//     value: string;
//     onChange: (value: string) => void;
//     placeholder: string;
//     editor: string;
// }

// export default function SimpleRichBox({
//     value,
//     onChange,
//     placeholder,
//     editor
// }: SimpleRichBoxProps) {
//     const textRef = useRef<HTMLTextAreaElement>(null);

//     const wrapSelection = (wrapper: string) => {
//         const textarea = textRef.current;
//         if (!textarea) return;

//         const selectionStart = textarea.selectionStart;
//         const selectionEnd = textarea.selectionEnd;

//         if (selectionStart === selectionEnd) return;

//         const before = value.substring(0, selectionStart);
//         const selected = value.substring(selectionStart, selectionEnd);
//         const after = value.substring(selectionEnd);

//         const wrapped = `${wrapper}${selected}${wrapper}`;
//         const newText = before + wrapped + after;
//         onChange(newText);

//         // Restore focus and position cursor after the wrapped text
//         setTimeout(() => {
//             textarea.focus();
//             textarea.selectionStart = textarea.selectionEnd = selectionStart + wrapped.length;
//         }, 0);
//     };

//     const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
//         onChange(e.target.value);
//     };

//     return (
//         <Box
//             sx={{
//                 border: "#d0d0d0",
//                 borderRadius: "8px",
//                 width: "100%",
//                 overflow: "hidden",
//             }}
//         >
//             {/* Toolbar */}
//             <Box
//                 sx={{
//                     display: "flex",
//                     alignItems: "center",
//                     borderBottom: "1px solid #ccc",
//                     px: 1,
//                     py: 1,
//                     backgroundColor: "#ffffff",
//                 }}
//             >
//                 <IconButton onClick={() => wrapSelection("*")} size="small" title="Bold">
//                     <FormatBoldIcon fontSize="small" />
//                 </IconButton>
//                 <IconButton onClick={() => wrapSelection("_")} size="small" title="Italic">
//                     <FormatItalicIcon fontSize="small" />
//                 </IconButton>
//                 <IconButton onClick={() => wrapSelection("~")} size="small" title="Underline">
//                     <FormatUnderlinedIcon fontSize="small" />
//                 </IconButton>
//             </Box>

//             {/* Text Field */}
//             <TextField
//                 multiline
//                 minRows={3}
//                 fullWidth
//                 inputRef={textRef}
//                 value={value}
//                 onChange={handleChange}
//                 placeholder={placeholder}
//                 variant="standard"
//                 InputProps={{
//                     disableUnderline: true,
//                     sx: {
//                         px: 2,
//                         py: 1,
//                         backgroundColor: "#fcfcfc",
//                     },
//                 }}
//                 sx={{
//                     "& .MuiInputBase-root": {
//                         borderRadius: 0,
//                     },
//                 }}
//             />

//         </Box>
//     );
// }
